import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ArticleCard from "@/components/news/ArticleCard";
import { Reveal } from "@/components/motion/Reveal";
import { newsCategories, categoryById, sortedArticles, articlesByCategory } from "@/data/articles";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";

export default function News() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const active = categoryById(activeCat);
  const all = useMemo(sortedArticles, []);
  const featured = all.find((a) => a.featured) || all[0];

  const setCat = (id) => {
    if (id === "all") setParams({});
    else setParams({ cat: id });
    window.__lenis ? window.__lenis.scrollTo("#news-feed", { offset: -90 }) : document.getElementById("news-feed")?.scrollIntoView({ behavior: "smooth" });
  };

  const title = active ? `${active.label} Articles — Service News` : "Service News & Repair Guides — Birmingham AL";
  const description = active
    ? `${active.blurb} Plain-English guides from the technicians at Auto Basic Care in Birmingham, AL.`
    : "Straight-talk articles on suspension, air conditioning, brakes, diagnostics, oil, tires, batteries, and cooling systems from Auto Basic Care in Birmingham, AL.";

  return (
    <div className="abc-app">
      <Seo
        path={active ? `/news?cat=${active.id}` : "/news"}
        title={title}
        description={description}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "News", path: "/news" }])]}
      />
      <SiteHeader />

      <section className="financing-hero news-hero">
        <div className="container financing-hero-inner">
          <div className="badge">News Forum</div>
          <h1>Straight talk from the service bay.</h1>
          <p className="financing-hero-p">
            What's actually wrong, what it actually costs, and what you can safely wait on. Guides written by
            the technicians at Auto Basic Care — organized by the service you're asking about.
          </p>
          <div className="button-row">
            <Link to="/#schedule" className="btn btn-primary" data-testid="news-hero-book">Schedule Service</Link>
            <a href="tel:2054051238" className="btn btn-outline-light" data-testid="news-hero-call">Call 205-405-1238</a>
          </div>
        </div>
      </section>

      <main>
        {activeCat === "all" && featured && (
          <section className="section news-featured-section">
            <div className="container">
              <Reveal>
                <div className="eyebrow">Featured Article</div>
                <ArticleCard article={featured} variant="featured" />
              </Reveal>
            </div>
          </section>
        )}

        <section className="section news-feed" id="news-feed">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">Browse by Service</div>
                <h2 className="section-title">{active ? active.label : "Every topic, every service."}</h2>
                {active && <p className="muted news-cat-blurb">{active.blurb}</p>}
              </div>
            </div>

            <div className="faq-categories news-categories" data-testid="news-categories">
              <button
                className={`faq-cat-pill ${activeCat === "all" ? "active" : ""}`}
                onClick={() => setCat("all")}
                data-testid="news-cat-all"
              >
                All Articles
              </button>
              {newsCategories.map((c) => (
                <button
                  key={c.id}
                  className={`faq-cat-pill ${activeCat === c.id ? "active" : ""}`}
                  onClick={() => setCat(c.id)}
                  data-testid={`news-cat-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {active ? (
              <div className="news-grid" data-testid="news-grid-filtered">
                {articlesByCategory(active.id).map((a, i) => (
                  <Reveal key={a.slug} delay={i * 0.06}>
                    <ArticleCard article={a} />
                  </Reveal>
                ))}
              </div>
            ) : (
              newsCategories.map((c) => {
                const items = articlesByCategory(c.id);
                if (!items.length) return null;
                return (
                  <div key={c.id} className="news-group" id={`news-${c.id}`} data-testid={`news-group-${c.id}`}>
                    <div className="news-group-head">
                      <div>
                        <h3 className="news-group-title">{c.label}</h3>
                        <p className="muted">{c.blurb}</p>
                      </div>
                      <button className="news-group-link" onClick={() => setCat(c.id)} data-testid={`news-group-view-${c.id}`}>
                        View all {c.label} →
                      </button>
                    </div>
                    <div className="news-grid">
                      {items.map((a, i) => (
                        <Reveal key={a.slug} delay={i * 0.06}>
                          <ArticleCard article={a} />
                        </Reveal>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="tires-cta">
          <div className="container">
            <h2>Read something that sounds like your car?</h2>
            <p>Book the inspection online in under two minutes, or call and talk to a technician.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/#schedule" className="btn btn-dark" data-testid="news-cta-book">Book Appointment</Link>
              <a href="tel:2054051238" className="btn btn-outline-light" data-testid="news-cta-call">Call 205-405-1238</a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
